import { useState } from 'react';
import { useInView } from '../../hooks/useInView';

const FORM_URL = 'https://readdy.ai/api/form/d70msvj2m2156g4s6vrg';

const services = [
  'Corporate Video Production',
  'Brand Story Film',
  'Live Streaming Services',
  'Event Coverage',
  'Photography Services',
  'Creative Direction & Editing',
  'Multiple Services',
];

function ContactHero() {
  return (
    <section className="relative overflow-hidden bg-[#020202]">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/01d8b4d1fba1d12755ae0686ca488775.jpg"
          alt="Contact Immanuel Production Group for a production consultation"
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020202]/70 to-[#020202]" />
        <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none" />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-8 lg:px-20 pt-52 pb-24 text-center">
        <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Get In Touch</p>
        <h1 className="font-serif font-bold text-white leading-tight mb-6" style={{ fontSize: 'clamp(3rem, 7vw, 6.5rem)', letterSpacing: '-0.03em' }}>
          Let's Start Your<br />
          <em className="not-italic text-gold">Production Journey</em>
        </h1>
        <p className="font-sans text-lg text-white/45 font-light leading-relaxed max-w-2xl mx-auto">
          Tell us about your project and we'll craft a broadcast-grade production strategy that transforms your vision into cinematic reality.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
          {['Response within 24 hours','Oviedo, Central Florida','Travel Available Worldwide'].map((t) => (
            <div key={t} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-gold rounded-full" />
              <span className="font-sans text-xs text-white/35 tracking-wide">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const { ref, inView } = useInView();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); setCharCount(0); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <section ref={ref} className="bg-[#080808] py-24">
      <div className={`max-w-7xl mx-auto px-8 lg:px-20 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left info panel */}
          <div className="lg:col-span-2">
            <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Contact</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Request a Production<br />
              <em className="not-italic text-gold">Consultation</em>
            </h2>
            <div className="w-10 h-px bg-gold mb-10" />
            <div className="space-y-7">
              {[
                { icon: 'ri-map-pin-2-line', label: 'Headquarters', val: 'Oviedo, Central Florida, FL' },
                { icon: 'ri-mail-line', label: 'Email', val: 'info@immanuelproductiongroup.com' },
                { icon: 'ri-time-line', label: 'Response Time', val: 'Within 24 hours' },
                { icon: 'ri-plane-line', label: 'Travel', val: 'Nationwide & International' },
                { icon: 'ri-global-line', label: 'Languages', val: 'English & Spanish' },
              ].map(({ icon, label, val }) => (
                <div key={label} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 flex items-center justify-center border border-gold/25 group-hover:border-gold/55 flex-shrink-0 transition-colors duration-300">
                    <i className={`${icon} text-gold text-base`} />
                  </div>
                  <div>
                    <p className="font-sans text-[0.58rem] text-gold uppercase tracking-[0.22em] mb-1">{label}</p>
                    <p className="font-sans text-sm text-white/45">{val}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Authority signal */}
            <div className="mt-10 p-6 border border-gold/12 bg-gold/4">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold text-xs" />
                ))}
              </div>
              <p className="font-sans text-xs text-white/40 leading-relaxed italic">
                &ldquo;Trusted by McDonald's, Disney, ABC, FOX News, and 500+ organizations across Central Florida and nationwide.&rdquo;
              </p>
              <p className="font-sans text-[0.6rem] text-gold/50 mt-2 tracking-wide">Est. 2005 &nbsp;&bull;&nbsp; 20+ Years of Excellence</p>
            </div>
          </div>

          {/* Form panel */}
          <div className="lg:col-span-3 border border-white/8 bg-[#0c0c0c] p-10 lg:p-12">
            {status === 'success' ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center border-2 border-gold mx-auto mb-6">
                  <i className="ri-check-line text-3xl text-gold" />
                </div>
                <h3 className="font-serif text-4xl text-white mb-5">Thank You</h3>
                <div className="w-10 h-px bg-gold mx-auto mb-5" />
                <p className="font-sans text-sm text-white/45 leading-relaxed max-w-xs mx-auto">We've received your consultation request and will be in touch within 24 hours to discuss your project.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="mb-8">
                  <p className="font-sans text-[0.58rem] tracking-[0.4em] uppercase text-gold font-medium mb-1">Production Consultation Request</p>
                  <p className="font-sans text-xs text-white/28">Fill out the form and we'll craft a production strategy for your goals.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/65 block mb-2">First Name *</label>
                    <input name="first_name" type="text" required className="w-full bg-[#0a0a0a] border border-white/8 text-sm text-white px-4 py-3.5 focus:outline-none focus:border-gold/45 transition-colors font-sans placeholder-white/20" placeholder="John" />
                  </div>
                  <div>
                    <label className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/65 block mb-2">Last Name *</label>
                    <input name="last_name" type="text" required className="w-full bg-[#0a0a0a] border border-white/8 text-sm text-white px-4 py-3.5 focus:outline-none focus:border-gold/45 transition-colors font-sans placeholder-white/20" placeholder="Smith" />
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/65 block mb-2">Email Address *</label>
                  <input name="email" type="email" required className="w-full bg-[#0a0a0a] border border-white/8 text-sm text-white px-4 py-3.5 focus:outline-none focus:border-gold/45 transition-colors font-sans placeholder-white/20" placeholder="john@company.com" />
                </div>
                <div>
                  <label className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/65 block mb-2">Company / Organization</label>
                  <input name="company" type="text" className="w-full bg-[#0a0a0a] border border-white/8 text-sm text-white px-4 py-3.5 focus:outline-none focus:border-gold/45 transition-colors font-sans placeholder-white/20" placeholder="Your Company" />
                </div>
                <div>
                  <label className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/65 block mb-2">Service Needed *</label>
                  <select name="service" required className="w-full bg-[#0a0a0a] border border-white/8 text-sm text-white/60 px-4 py-3.5 focus:outline-none focus:border-gold/45 transition-colors font-sans cursor-pointer">
                    <option value="">Select a service...</option>
                    {services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/65 block mb-2">Project Description *</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#0a0a0a] border border-white/8 text-sm text-white px-4 py-3.5 focus:outline-none focus:border-gold/45 transition-colors font-sans resize-none placeholder-white/20"
                    placeholder="Describe your vision, goals, timeline, and any relevant details about the project..."
                  />
                  <div className="flex justify-end mt-1">
                    <span className={`font-sans text-xs ${charCount > 480 ? 'text-red-400' : 'text-white/22'}`}>{charCount}/500</span>
                  </div>
                </div>
                {status === 'error' && (
                  <p className="font-sans text-xs text-red-400">Something went wrong. Please try again or email us directly.</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="whitespace-nowrap w-full font-sans text-sm font-semibold tracking-[0.18em] uppercase py-5 bg-gold text-[#020202] hover:bg-white disabled:opacity-50 transition-colors duration-300 cursor-pointer"
                >
                  {status === 'sending' ? 'Sending...' : 'Request Production Consultation'}
                </button>
                <p className="font-sans text-[0.6rem] text-white/22 text-center">Response within 24 hours &nbsp;&bull;&nbsp; Oviedo, Central Florida</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <main>
      <ContactHero />
      <ContactForm />
    </main>
  );
}
